// frontend/src/pages/admin/HandbookManagement.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, FilePlus2, Save, Send, CheckCircle2, UploadCloud, History,
  Trash2, ArrowUp, ArrowDown, Plus, GitCompare, Eye, AlertTriangle,
  Clock, Lock, Layers, ChevronRight, Users, CalendarDays, Tag, Sparkles,
  FileStack,
} from 'lucide-react';
import toast from '../../utils/toast';
import {
  useHandbookSections,
  useHandbookSectionRevisions,
  useHandbookRevision,
  useCreateHandbookDraft,
  useUpdateHandbookRevision,
  useHandbookRevisionTransition,
  useHandbookEditions,
  useCreateHandbookEdition,
  useUpdateHandbookEdition,
  usePublishHandbookEdition,
} from '../../hooks/useHandbookData';
import { StatusBadge } from '../../components/UI/StatusBadge';

// --- shared constants -------------------------------------------------------

const APPLIES_TO_OPTIONS = [
  { value: 'all', label: 'All employees' },
  { value: 'staff', label: 'Office staff' },
  { value: 'worker', label: 'Workers, Foremen & Supervisors' },
];

const CALLOUT_TONES = [
  { value: 'info', label: 'Info (blue)' },
  { value: 'warning', label: 'Warning (amber)' },
  { value: 'success', label: 'Success (green)' },
];

const OL_STYLES = [
  { value: 'decimal', label: '1. 2. 3.' },
  { value: 'roman', label: 'i. ii. iii.' },
  { value: 'alpha', label: 'a. b. c.' },
];

const BLOCK_TYPES = [
  { t: 'p', label: 'Paragraph' },
  { t: 'h', label: 'Heading' },
  { t: 'ul', label: 'Bullet list' },
  { t: 'ol', label: 'Numbered list' },
  { t: 'table', label: 'Table' },
  { t: 'callout', label: 'Callout' },
  { t: 'links', label: 'Links' },
];

const EDITABLE = ['draft', 'in_review', 'approved'];

const newBlock = (t) => {
  switch (t) {
    case 'p': return { t: 'p', text: '' };
    case 'h': return { t: 'h', text: '' };
    case 'ul': return { t: 'ul', items: [''] };
    case 'ol': return { t: 'ol', style: 'decimal', items: [''] };
    case 'callout': return { t: 'callout', tone: 'info', title: '', text: '' };
    case 'table': return {
      t: 'table',
      headers: [{ text: 'Column 1' }, { text: 'Column 2' }],
      rows: [['', '']],
    };
    case 'links': return { t: 'links', items: [{ label: '', href: '' }] };
    default: return { t: 'p', text: '' };
  }
};

// Flatten a block to plain text for the compare view.
const blockToText = (b) => {
  if (!b) return '';
  switch (b.t) {
    case 'p': return b.text || '';
    case 'h': return `# ${b.text || ''}`;
    case 'ul': return (b.items || []).map((i) => `- ${i}`).join('\n');
    case 'ol': return (b.items || []).map((i, idx) => `${idx + 1}. ${i}`).join('\n');
    case 'callout': return `[${b.tone}] ${b.title ? b.title + ' - ' : ''}${b.text || ''}`;
    case 'table': {
      const headerLine = (b.headers || []).map((h) => (typeof h === 'string' ? h : h.text || '')).join(' | ');
      const rowLines = (b.rows || []).map((row) => row.join(' | ')).join('\n');
      return `${headerLine}\n${rowLines}`;
    }
    case 'links': return (b.items || []).map((l) => `${l.label} (${l.href})`).join('\n');
    default: return '';
  }
};

const EDITOR_TABS = [
  { id: 'edit', label: 'Edit', icon: BookOpen },
  { id: 'compare', label: 'Compare', icon: GitCompare },
  { id: 'history', label: 'History', icon: History },
];

// --- block editor card ------------------------------------------------------

const BlockCard = ({ block, index, total, onChange, onMove, onRemove, disabled }) => {
  const typeLabel = BLOCK_TYPES.find((x) => x.t === block.t)?.label || block.t;

  const itemsToText = (items) => (items || []).join('\n');
  const textToItems = (text) => text.split('\n').map((l) => l.replace(/^[-*]\s?/, '')).filter((l, i, a) => l !== '' || i < a.length);

  const linksToText = (items) => (items || []).map((l) => `${l.label} | ${l.href}`).join('\n');
  const textToLinks = (text) => text.split('\n').filter((l) => l.trim() !== '').map((line) => {
    const [label, href] = line.split('|').map((p) => (p || '').trim());
    return { label: label || '', href: href || '' };
  });

  const tableToText = (block) => {
    const headerLine = (block.headers || [])
      .map((h) => {
        if (typeof h === 'string') return h;
        const suffix = h.colSpan && h.colSpan > 1 ? ` (span ${h.colSpan})` : '';
        return `${h.text || ''}${suffix}`;
      })
      .join(' | ');
    const rowLines = (block.rows || []).map((row) => row.join(' | ')).join('\n');
    return `${headerLine}\n${rowLines}`;
  };

  const textToTable = (text) => {
    const lines = text.split('\n').filter((l) => l.trim() !== '');
    if (!lines.length) return { headers: [{ text: 'Column 1' }], rows: [['']] };
    const parseCells = (line) => line.split('|').map((p) => p.trim());
    const headerCells = parseCells(lines[0]).map((cell) => {
      const spanMatch = cell.match(/^(.+?)\s*\(span\s+(\d+)\)$/i);
      if (spanMatch) return { text: spanMatch[1].trim(), colSpan: Number(spanMatch[2]) };
      return { text: cell };
    });
    const rows = lines.slice(1).map(parseCells);
    return { headers: headerCells, rows: rows.length ? rows : [['']] };
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50 rounded-t-xl">
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{typeLabel}</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={disabled || index === 0}
            onClick={() => onMove(index, -1)}
            className="icon-button-admin disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Move block up"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            disabled={disabled || index === total - 1}
            onClick={() => onMove(index, 1)}
            className="icon-button-admin disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Move block down"
          >
            <ArrowDown className="w-4 h-4" />
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onRemove(index)}
            className="icon-button-danger disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Delete block"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="p-3 space-y-2">
        {(block.t === 'p' || block.t === 'h') && (
          <textarea
            className="textarea-admin w-full text-sm"
            rows={block.t === 'h' ? 1 : 3}
            placeholder={block.t === 'h' ? 'Heading text' : 'Paragraph text (use **bold** for emphasis)'}
            value={block.text || ''}
            disabled={disabled}
            onChange={(e) => onChange(index, { ...block, text: e.target.value })}
          />
        )}

        {(block.t === 'ul' || block.t === 'ol') && (
          <>
            {block.t === 'ol' && (
              <select className="select-admin text-sm w-44" value={block.style || 'decimal'} disabled={disabled}
                onChange={(e) => onChange(index, { ...block, style: e.target.value })}>
                {OL_STYLES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            <textarea
              className="textarea-admin w-full text-sm font-mono"
              rows={Math.max(2, (block.items || []).length)}
              placeholder="One item per line"
              value={itemsToText(block.items)}
              disabled={disabled}
              onChange={(e) => onChange(index, { ...block, items: textToItems(e.target.value) })}
            />
            <p className="text-[11px] text-gray-400">One list item per line.</p>
          </>
        )}

        {block.t === 'callout' && (
          <>
            <div className="flex gap-2">
              <select className="select-admin text-sm w-40" value={block.tone || 'info'} disabled={disabled}
                onChange={(e) => onChange(index, { ...block, tone: e.target.value })}>
                {CALLOUT_TONES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <input className="input-admin text-sm flex-1" placeholder="Title (optional)" value={block.title || ''} disabled={disabled}
                onChange={(e) => onChange(index, { ...block, title: e.target.value })} />
            </div>
            <textarea className="textarea-admin w-full text-sm" rows={2} placeholder="Callout text" value={block.text || ''} disabled={disabled}
              onChange={(e) => onChange(index, { ...block, text: e.target.value })} />
          </>
        )}

        {block.t === 'links' && (
          <>
            <textarea
              className="textarea-admin w-full text-sm font-mono"
              rows={Math.max(2, (block.items || []).length)}
              placeholder="Label | https://url"
              value={linksToText(block.items)}
              disabled={disabled}
              onChange={(e) => onChange(index, { ...block, items: textToLinks(e.target.value) })}
            />
            <p className="text-[11px] text-gray-400">One link per line as <span className="font-mono">Label | https://url</span></p>
          </>
        )}

        {block.t === 'table' && (
          <>
            <textarea
              className="textarea-admin w-full text-sm font-mono"
              rows={Math.max(4, (block.rows || []).length + 1)}
              placeholder={'Tier System | Age Range | Positions (span 3)\nTier 1 | 35 – 45 | Senior Managers | Assistant Managers | Managers'}
              value={tableToText(block)}
              disabled={disabled}
              onChange={(e) => onChange(index, { ...block, ...textToTable(e.target.value) })}
            />
            <p className="text-[11px] text-gray-400">
              First line = headers separated by <span className="font-mono">|</span>. Use <span className="font-mono">(span N)</span> for merged header cells. One data row per line after that.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// --- audit timeline ---------------------------------------------------------

const AuditTimeline = ({ sectionId }) => {
  const { data: revisions = [], isLoading } = useHandbookSectionRevisions(sectionId);
  if (isLoading) return <p className="text-sm text-gray-500">Loading history...</p>;
  if (!revisions.length) return <p className="text-sm text-gray-500">No revision history yet.</p>;
  const fmt = (d) => (d ? new Date(d).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' }) : '-');
  return (
    <ol className="space-y-3">
      {revisions.map((r) => (
        <li key={r.id} className="relative pl-5">
          <span className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-red-400" />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">v{r.version}</span>
            <StatusBadge status={r.status} />
            {r.is_current && <StatusBadge status="live" label="Live" />}
          </div>
          {r.change_summary && <p className="text-sm text-gray-600 mt-0.5">{r.change_summary}</p>}
          <p className="text-xs text-gray-400 mt-0.5">
            {r.author_name || 'Unknown'} - {fmt(r.updated_at)}
            {r.approved_by_name ? ` - approved by ${r.approved_by_name}` : ''}
          </p>
        </li>
      ))}
    </ol>
  );
};

// --- compare (live vs draft) ------------------------------------------------

const CompareView = ({ liveBlocks, draftBlocks }) => {
  const liveText = (liveBlocks || []).map(blockToText).join('\n\n');
  const draftText = (draftBlocks || []).map(blockToText).join('\n\n');
  const changed = liveText !== draftText;
  return (
    <div className="space-y-2">
      {!changed && (
        <p className="text-xs text-gray-500 flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" /> No content differences from the live version.
        </p>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Live (published)</p>
          <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{liveText || '(empty)'}</pre>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50/40 p-3">
          <p className="text-xs font-semibold text-red-500 mb-2 uppercase tracking-wide">This draft</p>
          <pre className="text-xs text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">{draftText || '(empty)'}</pre>
        </div>
      </div>
    </div>
  );
};

// --- editions panel ---------------------------------------------------------

const WHATS_NEW_KIND = {
  new: 'bg-blue-100 text-blue-700',
  updated: 'bg-amber-100 text-amber-700',
};

const EMPTY_EDITION = { code: '', title: 'Employee Handbook', effective_date: '', change_summary: '' };

const ChangeLogList = ({ changes }) => {
  if (!changes?.length) {
    return (
      <p className="text-xs text-gray-400 italic">
        No section-level changes were captured for this edition.
      </p>
    );
  }
  return (
    <ul className="space-y-1.5">
      {changes.map((c) => (
        <li key={c.sectionKey} className="flex items-start gap-2 text-sm">
          <span className={`inline-flex items-center text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded mt-0.5 ${WHATS_NEW_KIND[c.kind] || WHATS_NEW_KIND.updated}`}>
            {c.kind}
          </span>
          <span className="text-gray-700 leading-relaxed">
            {c.clauseNumber ? `${c.clauseNumber}. ` : ''}{c.title}
            {c.summary ? <span className="text-gray-500"> &mdash; {c.summary}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
};

const EditionsPanel = () => {
  const { data: editions = [], isLoading } = useHandbookEditions();
  const createEdition = useCreateHandbookEdition();
  const updateEdition = useUpdateHandbookEdition();
  const publishEdition = usePublishHandbookEdition();

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_EDITION);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [confirmPublishId, setConfirmPublishId] = useState(null);

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-SG', { dateStyle: 'medium' }) : '\u2014');

  const handleCreate = () => {
    if (!form.code.trim()) { toast.error('Edition code is required (e.g. 2026.06)'); return; }
    createEdition.mutate(form, {
      onSuccess: () => { setCreating(false); setForm(EMPTY_EDITION); },
    });
  };

  const startEdit = (ed) => {
    setEditingId(ed.id);
    setEditForm({
      code: ed.code,
      title: ed.title || 'Employee Handbook',
      effective_date: ed.effective_date || '',
      change_summary: ed.change_summary || '',
    });
  };

  const saveEdit = () => {
    updateEdition.mutate({ editionId: editingId, data: editForm }, { onSuccess: () => setEditingId(null) });
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <FileStack className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900">Editions</h2>
            <p className="text-xs text-gray-500">
              Publish a formal release. Publishing snapshots every live clause, records the change log, and triggers employee re-acknowledgement.
            </p>
          </div>
        </div>
        {!creating && (
          <button className="btn btn-admin-primary btn-sm flex-shrink-0" onClick={() => setCreating(true)}>
            <Plus className="w-4 h-4 mr-1.5" />New edition
          </button>
        )}
      </div>

      {/* Create form */}
      {creating && (
        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/60">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label-admin flex items-center gap-1.5"><Tag className="w-4 h-4 text-gray-400" /> Edition code</label>
              <input className="input-admin w-full" placeholder="e.g. 2026.06" value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label-admin flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-gray-400" /> Effective date</label>
              <input type="date" className="input-admin w-full" value={form.effective_date}
                onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} />
            </div>
            <div className="form-group sm:col-span-2">
              <label className="form-label-admin">Title</label>
              <input className="input-admin w-full" value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="form-group sm:col-span-2">
              <label className="form-label-admin">Summary of changes <span className="text-gray-400 font-normal">(shown in Document Control)</span></label>
              <textarea className="textarea-admin w-full h-20" placeholder="e.g. Restored sick-leave table and tier matrix; reordered clauses."
                value={form.change_summary} onChange={(e) => setForm((f) => ({ ...f, change_summary: e.target.value }))} />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button className="btn btn-admin-outline btn-sm" onClick={() => { setCreating(false); setForm(EMPTY_EDITION); }}>Cancel</button>
            <button className="btn btn-admin-primary btn-sm" onClick={handleCreate} disabled={createEdition.isPending}>
              <Save className="w-4 h-4 mr-1.5" />{createEdition.isPending ? 'Creating...' : 'Create draft'}
            </button>
          </div>
        </div>
      )}

      {/* Edition list */}
      <div className="p-5 space-y-3">
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading editions...</p>
        ) : editions.length === 0 ? (
          <p className="text-sm text-gray-500">No editions yet. Create one to publish a formal release.</p>
        ) : editions.map((ed) => {
          const isEditing = editingId === ed.id;
          const isDraft = ed.status === 'draft';
          return (
            <div key={ed.id} className="rounded-xl border border-gray-200">
              <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/60 rounded-t-xl">
                <span className="font-semibold text-gray-900 tabular-nums">{ed.code}</span>
                <StatusBadge status={ed.status} />
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5" /> Effective {fmtDate(ed.effective_date)}
                </span>
                <div className="ml-auto flex items-center gap-2">
                  {isDraft && !isEditing && (
                    <>
                      <button className="btn btn-admin-outline w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 btn-sm" onClick={() => startEdit(ed)}>
                        <Save className="w-4 h-4" /><span className="hidden sm:inline">Edit</span>
                      </button>
                      {confirmPublishId === ed.id ? (
                        <span className="inline-flex items-center gap-1.5">
                          <button className="btn btn-admin-outline btn-sm" onClick={() => setConfirmPublishId(null)}>Cancel</button>
                          <button className="btn btn-admin-primary btn-sm" disabled={publishEdition.isPending}
                            onClick={() => publishEdition.mutate(ed.id, { onSuccess: () => setConfirmPublishId(null) })}>
                            <CheckCircle2 className="w-4 h-4 mr-1.5" />Confirm publish
                          </button>
                        </span>
                      ) : (
                        <button className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 btn-sm" onClick={() => setConfirmPublishId(ed.id)}>
                          <UploadCloud className="w-4 h-4" /><span className="hidden sm:inline">Publish</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="p-4 space-y-3">
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="form-group">
                        <label className="form-label-admin">Edition code</label>
                        <input className="input-admin w-full" value={editForm.code}
                          onChange={(e) => setEditForm((f) => ({ ...f, code: e.target.value }))} />
                      </div>
                      <div className="form-group">
                        <label className="form-label-admin">Effective date</label>
                        <input type="date" className="input-admin w-full" value={editForm.effective_date}
                          onChange={(e) => setEditForm((f) => ({ ...f, effective_date: e.target.value }))} />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label-admin">Summary of changes</label>
                      <textarea className="textarea-admin w-full h-20" value={editForm.change_summary}
                        onChange={(e) => setEditForm((f) => ({ ...f, change_summary: e.target.value }))} />
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="btn btn-admin-outline btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                      <button className="btn btn-admin-primary btn-sm" onClick={saveEdit} disabled={updateEdition.isPending}>
                        <Save className="w-4 h-4 mr-1.5" />Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {ed.change_summary
                      ? <p className="text-sm text-gray-700 leading-relaxed">{ed.change_summary}</p>
                      : <p className="text-sm text-gray-400 italic">No edition summary.</p>}

                    {ed.status === 'published' && (
                      <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-3">
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          <Sparkles className="w-3.5 h-3.5" /> Changes in this edition
                        </p>
                        <ChangeLogList changes={ed.change_log} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- main page --------------------------------------------------------------

const HandbookManagement = () => {
  const { data: sections = [], isLoading } = useHandbookSections();
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState('edit'); // edit | history | compare
  const [view, setView] = useState('sections'); // sections | editions
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [showAddBlock, setShowAddBlock] = useState(false);

  const createDraft = useCreateHandbookDraft();
  const updateRevision = useUpdateHandbookRevision();
  const transition = useHandbookRevisionTransition();

  const selected = useMemo(() => sections.find((s) => s.id === selectedId) || null, [sections, selectedId]);

  // The revision we render: a working draft if one exists, else the live one.
  const draftSummary = selected?.latest_draft || null;
  const liveSummary = selected?.current_revision_detail || null;
  const activeRevId = draftSummary?.id || selected?.current_revision || null;
  const { data: activeRev } = useHandbookRevision(activeRevId);
  const { data: liveRev } = useHandbookRevision(selected?.current_revision || null);

  const isEditable = !!activeRev && EDITABLE.includes(activeRev.status);

  // Auto-select first section.
  useEffect(() => {
    if (!selectedId && sections.length) setSelectedId(sections[0].id);
  }, [sections, selectedId]);

  // Load the active revision into local form state.
  useEffect(() => {
    if (!activeRev) { setForm(null); return; }
    setForm({
      id: activeRev.id,
      title: activeRev.title || '',
      clause_number: activeRev.clause_number || '',
      applies_to: activeRev.applies_to || 'all',
      summary: activeRev.summary || '',
      change_summary: activeRev.change_summary || '',
      effective_from: activeRev.effective_from || '',
      blocks: Array.isArray(activeRev.blocks) ? activeRev.blocks : [],
    });
    setDirty(false);
    setTab('edit');
  }, [activeRev]);

  const grouped = useMemo(() => {
    const out = [];
    sections.forEach((s) => {
      let g = out.find((x) => x.part === s.part);
      if (!g) { g = { part: s.part, label: s.part_display || s.part, items: [] }; out.push(g); }
      g.items.push(s);
    });
    return out;
  }, [sections]);

  const patchForm = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };

  const changeBlock = (i, b) => patchForm({ blocks: form.blocks.map((x, idx) => (idx === i ? b : x)) });
  const moveBlock = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.blocks.length) return;
    const next = [...form.blocks];
    [next[i], next[j]] = [next[j], next[i]];
    patchForm({ blocks: next });
  };
  const removeBlock = (i) => patchForm({ blocks: form.blocks.filter((_, idx) => idx !== i) });
  const addBlock = (t) => { patchForm({ blocks: [...form.blocks, newBlock(t)] }); setShowAddBlock(false); };

  const handleCreateDraft = () => {
    if (!selected) return;
    // On success the new/resumed draft surfaces as latest_draft after the
    // ['handbook'] queries invalidate, and the form reloads via the activeRev effect.
    createDraft.mutate(selected.id, {
      onSuccess: () => toast.success('Draft ready to edit'),
    });
  };

  const handleSave = () => {
    if (!form?.id) return;
    const { id, ...data } = form;
    updateRevision.mutate({ revisionId: id, data });
    setDirty(false);
  };

  const handleTransition = (action) => {
    if (!form?.id) return;
    if (dirty && action !== 'publish') { toast.error('Save your draft first'); return; }
    const run = () => transition.mutate({ revisionId: form.id, action });
    if (dirty && action === 'publish') {
      // persist edits then publish
      const { id, ...data } = form;
      updateRevision.mutate({ revisionId: id, data }, { onSuccess: () => { setDirty(false); run(); } });
    } else {
      run();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 truncate">Employee Handbook</h1>
          </div>
          <p className="text-sm text-gray-500 ml-12">
            Edit policy content with a draft &rarr; review &rarr; approve &rarr; publish trail
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 sm:pt-1">
          <Link
            to="/admin/handbook/acknowledgements"
            className="btn btn-admin-outline w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
            aria-label="Acknowledgements"
          >
            <Users className="w-4 h-4" /><span className="hidden sm:inline">Acknowledgements</span>
          </Link>
          <Link
            to="/self-service/handbook"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-admin-outline w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2"
            aria-label="Preview reader"
          >
            <Eye className="w-4 h-4" /><span className="hidden sm:inline">Preview reader</span>
          </Link>
        </div>
      </div>

      {/* View switch — DS pill tabs (admin) */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide">
        {[
          { id: 'sections', label: 'Sections', icon: Layers },
          { id: 'editions', label: 'Editions', icon: FileStack },
        ].map((v) => {
          const Icon = v.icon;
          return (
            <button
              key={v.id}
              type="button"
              onClick={() => setView(v.id)}
              className={`tab-btn-admin ${view === v.id ? 'active' : ''}`}
              aria-pressed={view === v.id}
            >
              <Icon className="w-4 h-4" />
              {v.label}
            </button>
          );
        })}
      </div>

      {view === 'editions' && <EditionsPanel />}

      {view === 'sections' && (
      <div className="grid grid-cols-1 lg:grid-cols-[300px,1fr] gap-5">
        {/* Section list */}
        <aside className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-fit">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
            <Layers className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-700">Sections</span>
            <span className="ml-auto text-xs text-gray-400">{sections.length}</span>
          </div>
          <div className="max-h-[70vh] overflow-y-auto p-2">
            {isLoading ? (
              <p className="text-sm text-gray-500 p-3">Loading...</p>
            ) : grouped.map((g) => (
              <div key={g.part} className="mb-2">
                <p className="px-2 py-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{g.label}</p>
                {g.items.map((s) => {
                  const title = s.current_revision_detail?.title || s.latest_draft?.title || s.section_key;
                  const liveV = s.current_revision_detail?.version;
                  const active = s.id === selectedId;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSelectedId(s.id)}
                      className={`w-full text-left px-2.5 py-2 rounded-lg mb-0.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 ${active ? 'bg-red-50 text-red-700' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium truncate flex-1 ${active ? 'text-red-700' : 'text-gray-700'}`}>{title}</span>
                        {liveV ? <span className="text-[10px] text-gray-400">v{liveV}</span> : null}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        {s.current_revision_detail && <StatusBadge status="published" />}
                        {s.latest_draft && <StatusBadge status={s.latest_draft.status} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </aside>

        {/* Editor */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 min-h-[60vh]">
          {!selected ? (
            <div className="p-10 text-center text-gray-500">Select a section to begin.</div>
          ) : (
            <>
              {/* Editor header + actions */}
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400">{selected.part_display}</p>
                    <h2 className="text-lg font-bold text-gray-900">
                      {form?.clause_number ? `${form.clause_number}. ` : ''}{form?.title || selected.section_key}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      {liveSummary && <span className="text-xs text-gray-500">Live v{liveSummary.version}</span>}
                      {activeRev && <StatusBadge status={activeRev.status} />}
                      {!isEditable && activeRev?.status === 'published' && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Lock className="w-3 h-3" /> read-only</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {!draftSummary ? (
                      <button className="btn btn-admin-primary btn-sm" onClick={handleCreateDraft} disabled={createDraft.isPending}>
                        <FilePlus2 className="w-4 h-4 mr-1.5" />{createDraft.isPending ? 'Starting...' : 'New draft'}
                      </button>
                    ) : (
                      <>
                        <button className="btn btn-admin-outline btn-sm" onClick={handleSave} disabled={!dirty || updateRevision.isPending}>
                          <Save className="w-4 h-4 mr-1.5" />Save
                        </button>
                        {activeRev?.status === 'draft' && (
                          <button className="btn btn-admin-outline btn-sm" onClick={() => handleTransition('submit')} disabled={transition.isPending}>
                            <Send className="w-4 h-4 mr-1.5" />Submit
                          </button>
                        )}
                        {(activeRev?.status === 'draft' || activeRev?.status === 'in_review') && (
                          <button className="btn btn-admin-outline btn-sm" onClick={() => handleTransition('approve')} disabled={transition.isPending}>
                            <CheckCircle2 className="w-4 h-4 mr-1.5" />Approve
                          </button>
                        )}
                        <button className="btn btn-admin-primary btn-sm" onClick={() => handleTransition('publish')} disabled={transition.isPending}>
                          <UploadCloud className="w-4 h-4 mr-1.5" />Publish
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Tabs — DS pill tab pattern (admin) */}
                <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mt-4">
                  {EDITOR_TABS.map((t) => {
                    const Icon = t.icon;
                    const isActive = tab === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={`tab-btn-admin ${isActive ? 'active' : ''}`}
                        aria-pressed={isActive}
                      >
                        <Icon className="w-4 h-4" />
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Body */}
              <div className="p-5">
                {tab === 'history' && <AuditTimeline sectionId={selected.id} />}

                {tab === 'compare' && (
                  <CompareView liveBlocks={liveRev?.blocks} draftBlocks={form?.blocks} />
                )}

                {tab === 'edit' && form && (
                  <div className="space-y-5">
                    {!isEditable && (
                      <div className="flex gap-2 items-start rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                        <p className="text-sm text-amber-800">This is the published version and is read-only. Click <strong>New draft</strong> to propose changes.</p>
                      </div>
                    )}

                    {/* Meta fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="form-group">
                        <label className="form-label-admin">Clause number</label>
                        <input className="input-admin w-full" value={form.clause_number} disabled={!isEditable}
                          onChange={(e) => patchForm({ clause_number: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label className="form-label-admin">Applies to</label>
                        <select className="select-admin w-full" value={form.applies_to} disabled={!isEditable}
                          onChange={(e) => patchForm({ applies_to: e.target.value })}>
                          {APPLIES_TO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      <div className="form-group sm:col-span-2">
                        <label className="form-label-admin">Title</label>
                        <input className="input-admin w-full" value={form.title} disabled={!isEditable}
                          onChange={(e) => patchForm({ title: e.target.value })} />
                      </div>
                      <div className="form-group sm:col-span-2">
                        <label className="form-label-admin">Summary</label>
                        <textarea className="textarea-admin w-full h-20" value={form.summary} disabled={!isEditable}
                          onChange={(e) => patchForm({ summary: e.target.value })} />
                      </div>
                    </div>

                    {/* Blocks */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-700">Content blocks</h3>
                        {isEditable && (
                          <div className="relative">
                            <button className="btn btn-admin-outline btn-sm" onClick={() => setShowAddBlock((v) => !v)}>
                              <Plus className="w-4 h-4 mr-1" />Add block
                            </button>
                            {showAddBlock && (
                              <div className="absolute right-0 mt-1 z-10 bg-white rounded-xl shadow-lg border border-gray-100 py-1 w-44">
                                {BLOCK_TYPES.map((bt) => (
                                  <button
                                    key={bt.t}
                                    type="button"
                                    onClick={() => addBlock(bt.t)}
                                    className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-inset"
                                  >
                                    <ChevronRight className="w-3.5 h-3.5 text-gray-300" />{bt.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {form.blocks.length === 0 && <p className="text-sm text-gray-400">No content yet.</p>}
                      {form.blocks.map((b, i) => (
                        <BlockCard key={i} block={b} index={i} total={form.blocks.length}
                          onChange={changeBlock} onMove={moveBlock} onRemove={removeBlock} disabled={!isEditable} />
                      ))}
                    </div>

                    {/* Change summary (for the audit trail) */}
                    {isEditable && (
                      <div className="form-group pt-2 border-t border-gray-100">
                        <label className="form-label-admin flex items-center gap-1.5">
                          <Clock className="w-4 h-4 text-gray-400" /> Change summary <span className="text-gray-400 font-normal">(recorded in the audit trail)</span>
                        </label>
                        <textarea className="textarea-admin w-full h-16" placeholder="e.g. Updated off-in-lieu window to match portal (30-day claim, 90-day expiry)"
                          value={form.change_summary} onChange={(e) => patchForm({ change_summary: e.target.value })} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      </div>
      )}
    </div>
  );
};

export default HandbookManagement;
